import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppData } from "../context/AppDataContext";
import { api, type Challenge } from "../lib/api";
import { shareChallenge } from "../lib/share";
import PullToRefresh from "../components/PullToRefresh";
import Avatar from "../components/Avatar";

function ChallengesTab() {
  const navigate = useNavigate();
  const { allChallenges, balance, refresh, updateBalance, user } = useAppData();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showExpired, setShowExpired] = useState(false);

  // Get current user
  const currentUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

  const getStatusColor = (status: Challenge["status"]): string => {
    switch (status) {
      case "OPEN":
        return "green";
      case "LOCKED":
        return "yellow";
      case "RESOLVING":
        return "orange";
      case "SETTLED":
        return "blue";
      case "REFUNDED":
      case "EXPIRED":
        return "gray";
      default:
        return "gray";
    }
  };

  const getStatusLabel = (status: Challenge["status"]): string => {
    switch (status) {
      case "OPEN":
        return "Active";
      case "LOCKED":
        return "Locked";
      case "RESOLVING":
        return "Voting";
      case "SETTLED":
        return "Settled";
      case "REFUNDED":
        return "Refunded";
      case "EXPIRED":
        return "Expired";
      default:
        return status;
    }
  };

  // Get user's position and calculate TON change
  const getUserPosition = (challenge: Challenge) => {
    const positions = (challenge as any).positions || [];
    const userPosition = positions.find(
      (p: any) => p.user?.telegramId === currentUser?.id?.toString()
    );
    return userPosition;
  };

  const getTonChange = (challenge: Challenge): { amount: number; label: string } | null => {
    if (challenge.status !== "SETTLED" && challenge.status !== "REFUNDED" && challenge.status !== "EXPIRED") {
      return null;
    }

    const userPosition = getUserPosition(challenge);
    if (!userPosition) return null;

    const userStake = parseFloat(userPosition.amount);
    const userSide = userPosition.side;

    if (challenge.status === "REFUNDED" || challenge.status === "EXPIRED") {
      return { amount: 0, label: "±0" };
    }

    // SETTLED - check if user won
    if (challenge.outcome === userSide) {
      // Winner takes all (in 1v1)
      const poolTotal = parseFloat(challenge.poolTotal.toString());
      const profit = poolTotal - userStake;
      return { amount: profit, label: `+${profit.toFixed(1)}` };
    } else {
      // Loser
      return { amount: -userStake, label: `-${userStake.toFixed(1)}` };
    }
  };

  const formatTimeRemaining = (time: string | null): { text: string; isExpired: boolean } | null => {
    if (!time) return null;

    const target = new Date(time);
    const now = new Date();
    const diff = target.getTime() - now.getTime();

    if (diff <= 0) return { text: "Expired", isExpired: true };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h left`, isExpired: false };
    }

    if (hours > 0) {
      return { text: `${hours}h ${minutes}m left`, isExpired: false };
    }

    return { text: `${minutes}m left`, isExpired: false };
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleShare = (challenge: Challenge) => {
    shareChallenge(challenge.id);
  };

  const handleCancel = (challengeId: string) => {
    const doCancel = async () => {
      setCancellingId(challengeId);
      try {
        const result = await api.cancelChallenge(challengeId);
        // Update balance if returned
        if (result.userBalance !== null) {
          updateBalance(result.userBalance);
        }
        // Refresh challenges list
        await refresh();
      } catch (err) {
        console.error("Failed to cancel:", err);
        setError(err instanceof Error ? err.message : "Failed to cancel");
      } finally {
        setCancellingId(null);
      }
    };

    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.showConfirm(
        "Cancel this challenge? Your contribution will be refunded.",
        (confirmed) => {
          if (confirmed) doCancel();
        }
      );
    } else if (confirm("Cancel this challenge? Your contribution will be refunded.")) {
      doCancel();
    }
  };

  // Check if current user is the creator of a challenge
  const isCreator = (challenge: Challenge) => {
    return challenge.creator?.telegramId === currentUser?.id?.toString();
  };

  // Get opponent info from challenge
  const getOpponent = (challenge: Challenge): { username: string | null; photoUrl: string | null } => {
    const positions = (challenge as any).positions || [];
    // Find position that isn't the current user
    const opponentPosition = positions.find(
      (p: any) => p.user?.telegramId !== currentUser?.id?.toString()
    );
    if (opponentPosition?.user) {
      return {
        username: opponentPosition.user.telegramUsername,
        photoUrl: opponentPosition.user.telegramPhotoUrl
      };
    }
    return { username: null, photoUrl: null };
  };

  // Filter challenges based on showExpired toggle
  const filteredChallenges = showExpired
    ? allChallenges
    : allChallenges.filter(c => c.status !== "EXPIRED");

  return (
    <PullToRefresh onRefresh={refresh}>
      <div className="tab-content challenges">
        <div className="page-header">
          <div className="balance-badge">
            {balance.toFixed(2)} TON
          </div>
        </div>
        <div className="challenges-header">
          <h1>My Challenges</h1>
          <label className="toggle-label small">
            <input
              type="checkbox"
              checked={showExpired}
              onChange={(e) => setShowExpired(e.target.checked)}
            />
            Show expired
          </label>
        </div>

      {error && (
        <div className="error-banner dismissible" onClick={() => setError(null)}>
          {error}
          <span className="dismiss">x</span>
        </div>
      )}

      {filteredChallenges.length === 0 ? (
        <div className="empty-state">
          <p>{showExpired ? "No challenges yet" : "No active challenges"}</p>
          <p className="empty-hint">Tap Create to make your first challenge!</p>
        </div>
      ) : (
        <div className="challenge-list">
          {filteredChallenges.map((challenge) => {
            const expirationInfo = formatTimeRemaining(challenge.closeTime);
            const timerInfo = challenge.timerEndsAt ? formatTimeRemaining(challenge.timerEndsAt) : null;

            // Check if challenge has a timer (either duration or specific time)
            const hasTimer = !!challenge.timerEndsAt;
            const timerExpired = timerInfo?.isExpired || false;

            // Can resolve if: no timer OR timer has expired
            const canResolve = !hasTimer || timerExpired;

            return (
              <div
                key={challenge.id}
                className="challenge-item clickable"
                onClick={() => navigate(`/challenge/${challenge.id}`)}
              >
                {/* Matchup row */}
                {(() => {
                  const opponent = getOpponent(challenge);
                  return (
                    <div className="active-matchup">
                      <Avatar username={currentUser?.username || "You"} photoUrl={user?.telegramPhotoUrl} size="sm" />
                      <span className="matchup-you">You</span>
                      <span className="matchup-vs">vs</span>
                      {opponent.username ? (
                        <>
                          <Avatar username={opponent.username} photoUrl={opponent.photoUrl} size="sm" />
                          <span className="opponent-name">@{opponent.username}</span>
                        </>
                      ) : (
                        <>
                          <div className="avatar-placeholder-sm">?</div>
                          <span className="opponent-name waiting">?</span>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Description */}
                <p className="description">"{challenge.description}"</p>

                {/* Main row: Pool + Status + Action (all inline) */}
                <div className="card-main-row">
                  <div className="card-left">
                    <span className="pool-amount">{challenge.poolTotal} TON</span>
                    <span className={`status ${getStatusColor(challenge.status)}`}>
                      {getStatusLabel(challenge.status)}
                    </span>
                  </div>
                  <div className="card-right">
                    {challenge.status === "OPEN" && !expirationInfo?.isExpired && (
                      <>
                        <button
                          className="btn small secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShare(challenge);
                          }}
                        >
                          Share
                        </button>
                        {isCreator(challenge) && (
                          <button
                            className="btn small text"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancel(challenge.id);
                            }}
                            disabled={cancellingId === challenge.id}
                          >
                            {cancellingId === challenge.id ? "..." : "Cancel"}
                          </button>
                        )}
                      </>
                    )}
                    {challenge.status === "LOCKED" && canResolve && (
                      <button
                        className="btn small primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/resolve/${challenge.id}`);
                        }}
                      >
                        Resolve
                      </button>
                    )}
                    {challenge.status === "RESOLVING" && (
                      <button
                        className="btn small primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/resolve/${challenge.id}`);
                        }}
                      >
                        Vote
                      </button>
                    )}
                  </div>
                </div>

                {/* Status info row (timer, banner, outcome) */}
                {challenge.status === "OPEN" && expirationInfo && (
                  <div className="card-status-row">
                    <span className={`status-text ${expirationInfo.isExpired ? "expired" : ""}`}>
                      {expirationInfo.isExpired ? "Expired" : expirationInfo.text}
                    </span>
                  </div>
                )}

                {challenge.status === "LOCKED" && (
                  <div className="card-status-row">
                    {timerInfo && !timerInfo.isExpired && (
                      <span className="status-text timer">⏱️ {timerInfo.text}</span>
                    )}
                    {timerInfo?.isExpired && (
                      <span className="status-text ready">Time's up - Ready!</span>
                    )}
                    {!timerInfo && (
                      <span className="status-text ready">Ready to resolve</span>
                    )}
                  </div>
                )}

                {challenge.status === "RESOLVING" && (
                  <div className="card-status-row">
                    <span className="status-text">Waiting for votes...</span>
                  </div>
                )}

                {(challenge.status === "SETTLED" || challenge.status === "REFUNDED" || challenge.status === "EXPIRED") && (
                  <div className="card-status-row outcome">
                    <span className="outcome-text">
                      {challenge.status === "EXPIRED" ? "Expired" :
                       challenge.status === "REFUNDED" ? "Disputed - Refunded" :
                       `${challenge.outcome === "YES" ? (challenge.labelA || "YES") : (challenge.labelB || "NO")} wins`}
                    </span>
                    {(() => {
                      const tonChange = getTonChange(challenge);
                      if (!tonChange) return null;
                      const className = tonChange.amount > 0 ? "ton-change win" : tonChange.amount < 0 ? "ton-change loss" : "ton-change neutral";
                      return <span className={className}>{tonChange.label} TON</span>;
                    })()}
                  </div>
                )}

                {/* Date at bottom */}
                <div className="card-footer">
                  <span className="card-date">{formatDate(challenge.createdAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </PullToRefresh>
  );
}

export default ChallengesTab;
